import { Camera, Check, RefreshCw, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/language";
import { hapticImpact } from "@/lib/haptics";
import { cn } from "@/lib/utils";

// Ported from the reference chat-camera-dialog.tsx: live getUserMedia preview,
// capture a still to a canvas as JPEG, confirm/retake, camera flip.

type FacingMode = "user" | "environment";

// Captures a still frame from the live <video> and hands back a JPEG File that
// flows through the same upload path as a picked image.
const captureFrame = (video: HTMLVideoElement): Promise<File | null> =>
  new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx || !canvas.width || !canvas.height) {
      resolve(null);
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        resolve(
          new File([blob], `photo-${blob.size}.jpg`, { type: "image/jpeg" }),
        );
      },
      "image/jpeg",
      0.92,
    );
  });

export function CameraDialog({
  open,
  onClose,
  onCapture,
}: {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
}) {
  const { t } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<FacingMode>("environment");
  const [error, setError] = useState(false);
  // A captured-but-not-yet-sent still: its object URL for preview and the File
  // to upload on confirm. Null while the live camera is showing.
  const [preview, setPreview] = useState<{ url: string; file: File } | null>(
    null,
  );

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const clearPreview = () => {
    setPreview((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
  };

  // Acquire (or re-acquire, on camera flip) the stream while the dialog is open
  // and no still is pending. Tearing down on cleanup releases the device light.
  useEffect(() => {
    if (!open || preview) return;
    let cancelled = false;
    setError(false);
    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [open, facingMode, preview]);

  // Release everything the moment the dialog closes.
  useEffect(() => {
    if (open) return;
    stopStream();
    clearPreview();
    setFacingMode("environment");
  }, [open]);

  const handleCapture = async () => {
    const video = videoRef.current;
    if (!video) return;
    const file = await captureFrame(video);
    if (!file) return;
    // Shutter feedback, the way a camera app snaps.
    hapticImpact("medium");
    stopStream();
    setPreview({ url: URL.createObjectURL(file), file });
  };

  const handleSend = () => {
    if (!preview) return;
    onCapture(preview.file);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="tw:max-w-lg tw:gap-0 tw:overflow-hidden tw:border-0 tw:bg-black tw:p-0">
        <DialogTitle hideClose className="tw:sr-only">
          {t("Mandoob:Camera.Title")}
        </DialogTitle>
        <div className="tw:relative tw:flex tw:aspect-[3/4] tw:w-full tw:items-center tw:justify-center tw:bg-black">
          {error ? (
            <p className="tw:m-0 tw:px-8 tw:text-center tw:text-sm tw:text-white/80">
              {t("Mandoob:Camera.Blocked")}
            </p>
          ) : preview ? (
            <img
              src={preview.url}
              alt=""
              className="tw:size-full tw:object-contain"
            />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={cn(
                "tw:size-full tw:object-cover",
                facingMode === "user" && "tw:-scale-x-100",
              )}
            />
          )}

          <button
            type="button"
            aria-label={t("Mandoob:Back")}
            onClick={onClose}
            className="tw:absolute tw:end-3 tw:top-3 tw:flex tw:size-9 tw:items-center tw:justify-center tw:rounded-full tw:bg-black/45 tw:text-white tw:transition-colors tw:hover:bg-black/65"
          >
            <X className="tw:size-5" strokeWidth={2} />
          </button>

          {!preview && !error ? (
            <button
              type="button"
              aria-label={t("Mandoob:Camera.Flip")}
              onClick={() =>
                setFacingMode((mode) =>
                  mode === "user" ? "environment" : "user",
                )
              }
              className="tw:absolute tw:start-3 tw:top-3 tw:flex tw:size-9 tw:items-center tw:justify-center tw:rounded-full tw:bg-black/45 tw:text-white tw:transition-colors tw:hover:bg-black/65"
            >
              <RefreshCw className="tw:size-5" strokeWidth={2} />
            </button>
          ) : null}
        </div>

        <div className="tw:flex tw:items-center tw:justify-center tw:gap-6 tw:bg-black tw:px-6 tw:py-5">
          {error ? (
            <button
              type="button"
              onClick={onClose}
              className="tw:rounded-full tw:bg-white/15 tw:px-6 tw:py-2.5 tw:text-sm tw:font-semibold tw:text-white tw:transition-colors tw:hover:bg-white/25"
            >
              {t("Mandoob:Back")}
            </button>
          ) : preview ? (
            <>
              <button
                type="button"
                onClick={clearPreview}
                className="tw:rounded-full tw:bg-white/15 tw:px-6 tw:py-2.5 tw:text-sm tw:font-semibold tw:text-white tw:transition-colors tw:hover:bg-white/25"
              >
                {t("Mandoob:Camera.Retake")}
              </button>
              <button
                type="button"
                onClick={handleSend}
                aria-label={t("Mandoob:Camera.Use")}
                className="tw:flex tw:size-14 tw:items-center tw:justify-center tw:rounded-full tw:bg-brand tw:text-brand-foreground tw:transition-transform tw:hover:scale-105"
              >
                <Check className="tw:size-7" strokeWidth={2.5} />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleCapture}
              aria-label={t("Mandoob:Camera.Capture")}
              className="tw:flex tw:size-16 tw:items-center tw:justify-center tw:rounded-full tw:border-4 tw:border-white/70 tw:bg-white tw:text-black tw:transition-transform tw:hover:scale-105 tw:active:scale-95"
            >
              <Camera className="tw:size-7" strokeWidth={2} />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
